import { handleGetRequest } from '../../../utils/api-handler.util'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuth } from '../../../contexts/AuthContext'
import { useParams } from 'react-router-dom'
import { QueryKeys } from '../../../utils/queryKeyGenerator'
import { useNavigate } from 'react-router-dom'
import ProductImageCarousel from '../../../components/common/ProductImageCarousel'
import BackButtonAndTitle from '../../../components/retailer-shop/BackButtonAndTitle'
import ConfirmModal from '../../../components/common/ConfirmModal'
import { useState } from 'react'
import { handleDeleteRequest } from '../../../utils/api-handler.util'
import { showToaster } from '../../../utils/show-toaster.util'
import useProductCategories from '../../../hooks/useProductCategories'
import DisplayForUserType from '../../../components/auth/DisplayForUserType'
import USER_TYPE from '../../../constants/userType'

const ViewProductsPage = () => {
  const { currentUser } = useAuth()
  const { productSlug } = useParams()
  const navigate = useNavigate()
  const { getCategoryNameById } = useProductCategories()

  const userType = currentUser?.userType

  const { data: responseData } = useQuery({
    queryKey: QueryKeys.shopProductDetails(userType, productSlug),
    enabled: !!userType && !!productSlug,
    queryFn: () => handleGetRequest(`/${userType}s/my-products/${productSlug}`),
  })

  const product = responseData?.data

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => handleDeleteRequest(`/${userType}s/my-products/${product.productId}`),
    onSuccess: () => {
      showToaster('success', 'Product deleted successfully!')
      QueryKeys.listShopProducts.invalidate(userType)
      setShowDeleteModal(false)
      navigate('/my-products')
    },
    onError: () => {
      showToaster('error', 'Failed to delete product. Please try again.')
    },
  })

  const handleDeleteProduct = async () => {
    await deleteMutation.mutateAsync()
  }

  if (!product) {
    return (
      <div className="main-container">
        <p className="text-gray-500">Loading product details...</p>
      </div>
    )
  }

  const handleEditClick = () => {
    navigate(`/${userType}/products/${productSlug}/edit`)
  }

  return (
    <div className="main-container min-h-dvh">
      <BackButtonAndTitle navigateTo={`/my-products`} title="Product Details" />

      <div className="lg:grid lg:grid-cols-2 lg:gap-8 xl:gap-12">
        {/* Left side - Product Images */}
        <div className="lg:sticky lg:top-8">
          <ProductImageCarousel productImages={product.productImages} />
        </div>

        {/* Right side - Product Details and Actions */}
        <div className="mt-4 lg:mt-0">
          <div className="space-y-4 lg:space-y-6 mb-6 lg:mb-8">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start w-full gap-2 lg:gap-4">
              <h2 className="font-semibold text-gray-800 text-lg lg:text-2xl flex-1">
                {product.name}
              </h2>
              <span className="text-xl lg:text-3xl text-red-600 font-bold">
                {product.priceText}
              </span>
            </div>

            <p className="text-sm lg:text-base text-gray-500 leading-relaxed">
              {product.description}
            </p>

            <div className="flex flex-col gap-3 lg:gap-4">
              <span className="inline-flex items-center px-3 py-2 rounded-lg text-sm lg:text-base font-medium bg-blue-100 text-blue-800 w-fit">
                {getCategoryNameById(product.categoryId) || 'Uncategorized'}
              </span>

              <DisplayForUserType userTypes={[USER_TYPE.wholeseller]}>
                <div className="bg-neutral-200-50 p-3 lg:p-4 rounded-lg">
                  <p className="text-sm lg:text-base">
                    <span className="font-semibold">Minimum Order Quantity:</span>{' '}
                    <span className="text-gray-700">{product.minOrderQuantity}</span>
                  </p>
                </div>
              </DisplayForUserType>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
            <button
              onClick={handleEditClick}
              className="flex-1 bg-gradient-to-r from-orange-400 to-yellow-600 hover:from-orange-500 hover:to-yellow-700 text-white py-3 lg:py-4 rounded-lg lg:rounded-xl text-sm lg:text-base font-medium transition-all duration-200 hover:shadow-lg"
            >
              Edit Product
            </button>
            <button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 lg:py-4 rounded-lg lg:rounded-xl text-sm lg:text-base font-medium transition-all duration-200 hover:shadow-lg"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Product
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Product?"
        message="Are you sure you want to delete this product? This action cannot be undone."
        onConfirm={handleDeleteProduct}
      />
    </div>
  )
}

export default ViewProductsPage
